import React from "react";
import SidebarDropdown from "./SidebarDropdown";

interface FilterDropdown {
  id: string;
  label: string;
  icon?: string;
  options: string[];
}

interface SidebarProps {
  filterOptions: {
    dropdowns: FilterDropdown[];
    dataIntegrity: string;
  };
  pendingFilters: Record<string, string>;
  appliedFilters: Record<string, string> | null;
  onPendingChange: (id: string, value: string) => void;
  onApply: () => void;
  onReset: () => void;
  showExportPdf?: boolean;
  onExportPdf?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  filterOptions,
  pendingFilters,
  appliedFilters,
  onPendingChange,
  onApply,
  onReset,
  showExportPdf = false,
  onExportPdf,
}) => {
  const changedCount = appliedFilters
    ? Object.keys(pendingFilters).filter((k) => pendingFilters[k] !== appliedFilters[k]).length
    : 0;
  return (
    <aside className="sidebar">
      {/* ── Global Filters heading ──────────────── */}
      <div className="sidebar-filters-header">
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
          <path
            d="M1 2.5h9M2.5 5.5h6M4 8.5h3"
            stroke="#1F8CF9"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
        <span>Global Filters</span>
      </div>

      <div className="sidebar-divider" />

      {/* ── Dropdown filters ────────────────────── */}
      <div className="sidebar-dropdowns">
        {filterOptions.dropdowns.map((dd) => (
          <SidebarDropdown
            key={dd.id}
            label={dd.label}
            options={dd.options}
            value={pendingFilters[dd.id] ?? dd.options[0]}
            onChange={(value) => onPendingChange(dd.id, value)}
          />
        ))}
      </div>

      {/* ── Apply / Reset buttons ───────────────── */}
      <div className="sidebar-actions">
        <button className="sidebar-reset-btn" onClick={onReset}>
          Reset All
        </button>
        <button className="sidebar-apply-btn" onClick={onApply}>
          Apply
          {changedCount > 0 && (
            <span className="sidebar-apply-badge">{changedCount}</span>
          )}
        </button>
      </div>

      {/* ── Data Integrity footer ───────────────── */}
      <div className="sidebar-integrity">
        <div className="sidebar-integrity-header">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 1.5L2.5 4v4c0 3 2.5 5.2 5.5 6 3-0.8 5.5-3 5.5-6V4L8 1.5z"
              fill="#1a3f7a"
              fillOpacity="0.12"
              stroke="#1a3f7a"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
            <path
              d="M5.5 8l1.8 1.8 3.2-3.2"
              stroke="#1a3f7a"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="sidebar-integrity-label">DATA INTEGRITY</span>
        </div>
        <p className="sidebar-integrity-text">{filterOptions.dataIntegrity}</p>
      </div>

      {/* ── Export PDF (Variance Explorer tab only) ─ */}
      {showExportPdf && (
        <div className="sidebar-export-wrap">
          <button className="sidebar-export-btn" onClick={onExportPdf}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginRight: 6 }}>
              <path d="M6 1v7M3.5 5.5L6 8l2.5-2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1.5 9.5v1a0.5 0.5 0 0 0 .5.5h8a0.5 0.5 0 0 0 .5-.5v-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            Export PDF Report
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
